import React, { useState, useEffect } from 'react';
import { TrendingUp, Users, Star, Award, Brain, BarChart3, CheckCircle, XCircle, Activity, Clock, Trash2, RefreshCw, History } from 'lucide-react';

const NineBoxPotencialEnriquecido = () => {
  const [activeTab, setActiveTab] = useState('calcular');
  const [loading, setLoading] = useState(false);
  const [colaboradores, setColaboradores] = useState([]);
  const [ciclos, setCiclos] = useState([]);
  const [selectedColaborador, setSelectedColaborador] = useState('');
  const [selectedCiclo, setSelectedCiclo] = useState('');
  const [potencialData, setPotencialData] = useState(null);
  const [historicoResultados, setHistoricoResultados] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchColaboradores();
    fetchCiclos();
  }, []);

  useEffect(() => {
    if (selectedCiclo && activeTab === 'historico') {
      fetchHistorico();
    }
  }, [selectedCiclo, activeTab]);

  const fetchColaboradores = async () => {
    try {
      console.log('🔄 [9Box] Carregando colaboradores...');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/employees`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ [9Box] Colaboradores carregados:', data?.length || 0);
        setColaboradores(data);
      } else {
        console.error('❌ [9Box] Erro ao buscar colaboradores. Status:', response.status);
        const errorText = await response.text();
        console.error('Detalhes:', errorText);
      }
    } catch (error) {
      console.error('❌ [9Box] Erro ao buscar colaboradores:', error);
    }
  };

  const fetchCiclos = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/9box/matrizes`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.matrizes) {
          // Filtrar apenas matrizes ativas
          const matrizesAtivas = data.matrizes.filter(m => m.ativa);
          setCiclos(matrizesAtivas);
          if (matrizesAtivas.length > 0) {
            setSelectedCiclo(matrizesAtivas[0].id);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao buscar ciclos:', error);
    }
  };

  const calcularPotencial = async () => {
    if (!selectedColaborador) {
      setError('Por favor, selecione um colaborador');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/nine-box/calculate-enriched-potential`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            employee_id: selectedColaborador,
            cycle_id: selectedCiclo
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPotencialData(data);
      } else {
        const errorData = await response.json();
        let errorMessage = 'Erro ao calcular potencial';
        
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (errorData.detail) {
          errorMessage = JSON.stringify(errorData.detail);
        }
        
        setError(errorMessage);
      }
    } catch (error) {
      console.error('Erro ao calcular potencial:', error);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  const salvarPotencial = async () => {
    if (!selectedColaborador || !selectedCiclo) {
      setError('Por favor, selecione colaborador e ciclo');
      return;
    }

    if (!potencialData) {
      setError('Calcule o potencial primeiro antes de salvar');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/nine-box/update-position-with-enriched-potential?employee_id=${selectedColaborador}&cycle_id=${selectedCiclo}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        alert('✅ Potencial enriquecido salvo com sucesso!\n\nNova posição 9Box: ' + data.position);
        // Limpar dados e atualizar histórico
        setPotencialData(null);
        setSelectedColaborador('');
        if (selectedCiclo) {
          fetchHistorico();
        }
      } else {
        const errorData = await response.json();
        let errorMessage = 'Erro ao salvar potencial';
        
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (errorData.detail) {
          errorMessage = JSON.stringify(errorData.detail);
        }
        
        setError(errorMessage);
      }
    } catch (error) {
      console.error('Erro ao salvar potencial:', error);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistorico = async () => {
    if (!selectedCiclo) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/nine-box/enriched-potential/list/${selectedCiclo}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setHistoricoResultados(data.resultados || []);
      } else {
        console.error('Erro ao buscar histórico');
        setHistoricoResultados([]);
      }
    } catch (error) {
      console.error('Erro ao buscar histórico:', error);
      setHistoricoResultados([]);
    }
  };

  const excluirResultado = async (resultadoId) => {
    if (!window.confirm('Tem certeza que deseja excluir este resultado?')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/nine-box/enriched-potential/${resultadoId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        alert('✅ Resultado excluído com sucesso!');
        fetchHistorico();
      } else {
        alert('❌ Erro ao excluir resultado');
      }
    } catch (error) {
      console.error('Erro ao excluir resultado:', error);
      alert('❌ Erro ao conectar com o servidor');
    }
  };

  const getScoreColor = (score) => {
    if (score >= 4.5) return 'text-green-600';
    if (score >= 3.5) return 'text-blue-600';
    if (score >= 2.5) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBackground = (score) => {
    if (score >= 4.5) return 'bg-green-100';
    if (score >= 3.5) return 'bg-blue-100';
    if (score >= 2.5) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const getScoreLabel = (score) => {
    if (score >= 4.5) return 'Excepcional';
    if (score >= 3.5) return 'Alto';
    if (score >= 2.5) return 'Médio';
    return 'Baixo';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                9Box - Potencial Enriquecido
              </h1>
              <p className="text-gray-600 mt-2">
                Cálculo de potencial consolidando 6 fontes de dados: Competências, Feedbacks, Fit Cultural e PDI
              </p>
            </div>
            <Star className="w-16 h-16 text-indigo-600" />
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-lg mb-6">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('calcular')}
              className={`flex-1 px-6 py-4 font-medium transition-colors ${
                activeTab === 'calcular'
                  ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Calcular Potencial
              </div>
            </button>
            <button
              onClick={() => setActiveTab('historico')}
              className={`flex-1 px-6 py-4 font-medium transition-colors ${
                activeTab === 'historico'
                  ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <History className="w-5 h-5" />
                Histórico de Resultados
              </div>
            </button>
          </div>
        </div>

        {/* Aba: Calcular Potencial */}
        {activeTab === 'calcular' && (
          <>
            {/* Formulário */}
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-600" />
                Calcular Potencial Enriquecido
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Colaborador
                  </label>
                  <select
                    value={selectedColaborador}
                    onChange={(e) => setSelectedColaborador(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">Selecione um colaborador</option>
                    {colaboradores.map((colab) => (
                      <option key={colab.id} value={colab.id}>
                        {colab.nome || colab.name || 'Sem nome'}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ciclo 9Box
                  </label>
                  <select
                    value={selectedCiclo}
                    onChange={(e) => setSelectedCiclo(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">Selecione um ciclo</option>
                    {ciclos.map((ciclo) => (
                      <option key={ciclo.id} value={ciclo.id}>
                        {ciclo.name} - {ciclo.status}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                  <XCircle className="w-5 h-5" />
                  {error}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={calcularPotencial}
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Calculando...
                    </>
                  ) : (
                    <>
                      <BarChart3 className="w-5 h-5" />
                      Calcular Potencial
                    </>
                  )}
                </button>

                <button
                  onClick={salvarPotencial}
                  disabled={loading || !potencialData}
                  className="flex-1 bg-gradient-to-r from-green-600 to-teal-600 text-white py-3 rounded-lg hover:from-green-700 hover:to-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
                >
                  <Award className="w-5 h-5" />
                  Salvar e Atualizar 9Box
                </button>
              </div>
            </div>

            {/* Resultado */}
            {potencialData && (
              <>
            {/* Score Final */}
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg p-8 mb-6 text-white">
              <div className="text-center">
                <div className="text-lg font-medium mb-2">Potencial Enriquecido Final</div>
                <div className="text-6xl font-bold mb-2">
                  {potencialData.potencial_final.toFixed(2)}
                </div>
                <div className="text-xl opacity-90">
                  de 5.0 - {getScoreLabel(potencialData.potencial_final)}
                </div>
                <div className="mt-4 flex flex-col gap-2">
                  <div className="inline-block px-4 py-2 bg-white bg-opacity-20 rounded-lg backdrop-blur-sm">
                    {potencialData.modulos_com_dados || 0} de {potencialData.total_modulos || 8} módulos avaliados ({potencialData.percentual_completude || 0}%)
                  </div>
                  {potencialData.fator_penalizacao && potencialData.fator_penalizacao < 1.0 && (
                    <div className="text-sm bg-yellow-500 bg-opacity-20 px-3 py-1 rounded">
                      ⚠️ Nota reduzida em {potencialData.reducao_percentual || ((1 - potencialData.fator_penalizacao) * 100).toFixed(0)}% por falta de dados
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Módulos Avaliados - NOVO */}
            {potencialData.modulos_avaliados && potencialData.modulos_avaliados.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Módulos Consultados
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                  Complete todos os módulos abaixo para obter uma avaliação de potencial mais precisa e aumentar sua nota!
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {potencialData.modulos_avaliados.map((modulo, idx) => (
                    <div
                      key={idx}
                      className={`border rounded-lg p-4 ${
                        modulo.tem_dados
                          ? 'bg-green-50 border-green-200'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {modulo.tem_dados ? (
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            ) : (
                              <XCircle className="w-5 h-5 text-gray-400" />
                            )}
                            <span className={`font-medium ${modulo.tem_dados ? 'text-gray-800' : 'text-gray-500'}`}>
                              {modulo.nome}
                            </span>
                          </div>
                          <div className="text-xs text-gray-600 ml-7">
                            {modulo.detalhes}
                          </div>
                        </div>
                        {modulo.tem_dados && (
                          <div className="text-right">
                            <div className={`text-lg font-bold ${getScoreColor(modulo.score)}`}>
                              {modulo.score.toFixed(1)}
                            </div>
                            <div className="text-xs text-gray-500">score</div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Análise do Cálculo */}
            {potencialData.potencial_base && (
              <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-indigo-600" />
                  Detalhes do Cálculo
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Potencial Base */}
                  <div className="border rounded-lg p-4 bg-blue-50">
                    <div className="text-sm text-gray-600 mb-1">Potencial Base (sem penalização)</div>
                    <div className="text-3xl font-bold text-blue-600">
                      {potencialData.potencial_base.toFixed(2)}
                    </div>
                  </div>
                  
                  {/* Fator de Penalização */}
                  <div className="border rounded-lg p-4 bg-yellow-50">
                    <div className="text-sm text-gray-600 mb-1">Fator de Penalização</div>
                    <div className="text-3xl font-bold text-yellow-600">
                      {(potencialData.fator_penalizacao * 100).toFixed(0)}%
                    </div>
                  </div>
                  
                  {/* Potencial Final */}
                  <div className="border rounded-lg p-4 bg-green-50">
                    <div className="text-sm text-gray-600 mb-1">Potencial Final</div>
                    <div className="text-3xl font-bold text-green-600">
                      {potencialData.potencial_final.toFixed(2)}
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-4 bg-indigo-50 rounded-lg">
                  <div className="text-sm text-indigo-900">
                    <strong>💡 Como melhorar sua nota:</strong> Complete os módulos indicados como "Sem dados" acima para aumentar sua completude e reduzir a penalização aplicada ao cálculo.
                  </div>
                </div>
              </div>
            )}
              </>
            )}

            {/* Estado Vazio */}
            {!potencialData && !loading && (
              <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                <Star className="w-20 h-20 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">
                  Nenhum Cálculo Realizado
                </h3>
                <p className="text-gray-500">
                  Selecione um colaborador e clique em "Calcular Potencial" para ver a composição detalhada
                </p>
              </div>
            )}
          </>
        )}

        {/* Aba: Histórico de Resultados */}
        {activeTab === 'historico' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <History className="w-5 h-5 text-indigo-600" />
              Histórico de Potenciais Enriquecidos
            </h2>

            {/* Filtro de Ciclo */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filtrar por Ciclo
              </label>
              <select
                value={selectedCiclo}
                onChange={(e) => setSelectedCiclo(e.target.value)}
                className="w-full md:w-1/2 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">Selecione um ciclo</option>
                {ciclos.map((ciclo) => (
                  <option key={ciclo.id} value={ciclo.id}>
                    {ciclo.name} - {ciclo.status}
                  </option>
                ))}
              </select>
            </div>

            {/* Lista de Resultados */}
            {selectedCiclo ? (
              historicoResultados.length > 0 ? (
                <div className="space-y-4">
                  {historicoResultados.map((resultado) => (
                    <div
                      key={resultado.id}
                      className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          {/* Cabeçalho do Card */}
                          <div className="flex items-center gap-3 mb-3">
                            <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold ${getScoreBackground(resultado.potencial_final)}`}>
                              <span className={getScoreColor(resultado.potencial_final)}>
                                {resultado.potencial_final.toFixed(1)}
                              </span>
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-gray-800">
                                {resultado.colaborador_nome || 'Colaborador'}
                              </h3>
                              <p className="text-sm text-gray-500">
                                {resultado.colaborador_cargo || 'Cargo'} - {resultado.colaborador_departamento || 'Departamento'}
                              </p>
                            </div>
                          </div>

                          {/* Composição V2 - Nova estrutura */}
                          {resultado.modulos_avaliados && resultado.modulos_avaliados.length > 0 ? (
                            <div className="mb-3">
                              <div className="text-xs font-medium text-gray-600 mb-2">
                                Módulos avaliados: {resultado.modulos_com_dados || 0}/{resultado.total_modulos || 8} ({resultado.percentual_completude || 0}%)
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                {resultado.modulos_avaliados.filter(m => m.tem_dados).map((modulo, idx) => (
                                  <div key={idx} className="text-center bg-green-50 rounded p-2">
                                    <div className="text-xs text-gray-600 mb-1">{modulo.nome}</div>
                                    <div className={`text-sm font-bold ${getScoreColor(modulo.score)}`}>
                                      {modulo.score.toFixed(1)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            /* Composição V1 - Estrutura antiga (fallback) */
                            resultado.composicao && (
                              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
                                {resultado.composicao.competencias_base !== undefined && (
                                  <div className="text-center">
                                    <div className={`text-xs font-medium text-gray-600 mb-1`}>Competências</div>
                                    <div className={`text-lg font-bold ${getScoreColor(resultado.composicao.competencias_base)}`}>
                                      {resultado.composicao.competencias_base.toFixed(1)}
                                    </div>
                                  </div>
                                )}
                                {resultado.composicao.feedback_180 !== undefined && (
                                  <div className="text-center">
                                    <div className="text-xs font-medium text-gray-600 mb-1">Feedback 180º</div>
                                    <div className={`text-lg font-bold ${getScoreColor(resultado.composicao.feedback_180)}`}>
                                      {resultado.composicao.feedback_180.toFixed(1)}
                                    </div>
                                  </div>
                                )}
                                {resultado.composicao.feedback_360 !== undefined && (
                                  <div className="text-center">
                                    <div className="text-xs font-medium text-gray-600 mb-1">Feedback 360º</div>
                                    <div className={`text-lg font-bold ${getScoreColor(resultado.composicao.feedback_360)}`}>
                                      {resultado.composicao.feedback_360.toFixed(1)}
                                    </div>
                                  </div>
                                )}
                                {resultado.composicao.fit_cultural !== undefined && (
                                  <div className="text-center">
                                    <div className="text-xs font-medium text-gray-600 mb-1">Fit Cultural</div>
                                    <div className={`text-lg font-bold ${getScoreColor(resultado.composicao.fit_cultural)}`}>
                                      {resultado.composicao.fit_cultural.toFixed(1)}
                                    </div>
                                  </div>
                                )}
                                {resultado.composicao.multiplicador_pdi !== undefined && (
                                  <div className="text-center">
                                    <div className="text-xs font-medium text-gray-600 mb-1">Mult. PDI</div>
                                    <div className="text-lg font-bold text-purple-600">
                                      {resultado.composicao.multiplicador_pdi.toFixed(2)}x
                                    </div>
                                  </div>
                                )}
                              </div>
                            )
                          )}

                          {/* Info Adicional */}
                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {new Date(resultado.calculado_em).toLocaleDateString('pt-BR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                            {resultado.modulos_com_dados !== undefined ? (
                              <div className="flex items-center gap-1">
                                <CheckCircle className="w-4 h-4" />
                                {resultado.modulos_com_dados}/{resultado.total_modulos} módulos
                              </div>
                            ) : (
                              <div className="flex items-center gap-1">
                                <CheckCircle className="w-4 h-4" />
                                {Object.values(resultado.fontes_disponiveis || {}).filter(v => v).length} fontes
                              </div>
                            )}
                            {resultado.reducao_percentual !== undefined && resultado.reducao_percentual > 0 && (
                              <div className="flex items-center gap-1 text-yellow-600">
                                <Activity className="w-4 h-4" />
                                Penalização: {resultado.reducao_percentual}%
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Ações */}
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={() => excluirResultado(resultado.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Excluir"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <History className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">
                    Nenhum Resultado Encontrado
                  </h3>
                  <p className="text-gray-500">
                    Não há potenciais enriquecidos calculados para este ciclo ainda.
                  </p>
                </div>
              )
            ) : (
              <div className="text-center py-12">
                <Star className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-600 mb-2">
                  Selecione um Ciclo
                </h3>
                <p className="text-gray-500">
                  Escolha um ciclo 9Box para visualizar o histórico de potenciais calculados.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NineBoxPotencialEnriquecido;
